// frontend/src/contexts/AuthContext.js
// REFACTORED: Fixed state flickering, race conditions, and session management
// HIPAA-compliant authentication context with stable state management

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { toast } from "react-toastify";
import secureAxios from "../utils/secureAxios";
import SessionTimeoutWarning from "../components/authorizations/SessionTimeoutWarning";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// ðŸ”§ ENVIRONMENT-DRIVEN Session Configuration (NO HARDCODED VALUES!)
const REACT_TIMEOUT_MINUTES = 60;

console.log("FRONTEND DEBUG:", {
  raw: process.env.REACT_APP_HIPAA_TIMEOUT_MINUTES,
  parsed: parseInt(process.env.REACT_APP_HIPAA_TIMEOUT_MINUTES),
  final: REACT_TIMEOUT_MINUTES,
});

// Calculate ALL timing values from environment variables (NO HARDCODED MAXIMUMS!)
const SESSION_TOTAL_MS = REACT_TIMEOUT_MINUTES * 60 * 1000;
const WARNING_LEAD_TIME_MS = SESSION_TOTAL_MS * 0.2; // 20% of session time
const WARNING_START_MS = SESSION_TOTAL_MS - WARNING_LEAD_TIME_MS;
const CRITICAL_THRESHOLD_MS = WARNING_LEAD_TIME_MS * 0.5; // Half of warning time
const LOGOUT_TIMEOUT_MS = Math.max(SESSION_TOTAL_MS * 0.1, 5 * 1000); // 10% of session OR 5 sec min

console.log("â° SESSION TIMING CONFIGURATION (Environment-Driven):", {
  sessionTotalMinutes: REACT_TIMEOUT_MINUTES,
  sessionTotalMs: SESSION_TOTAL_MS,
  warningStartMs: WARNING_START_MS,
  warningDurationMs: WARNING_LEAD_TIME_MS,
  criticalThresholdMs: CRITICAL_THRESHOLD_MS,
  logoutTimeoutMs: LOGOUT_TIMEOUT_MS,
});

export const AuthProvider = ({ children }) => {
  const logoutInProgressRef = useRef(false);

  // ðŸ“Š STABLE AUTH STATE - Using a single state object to prevent race conditions
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    isLoading: true,
    isInitialized: false,
  });

  // ðŸ•’ HIPAA Session timeout state - USE ENVIRONMENT VARIABLE
  const timeoutMinutes = REACT_TIMEOUT_MINUTES;

  const [sessionState, setSessionState] = useState({
    showTimeoutWarning: false,
    timeRemaining: 0,
    timeoutStage: "warning",
  });

  // Debug session state changes
  const [debugMode] = useState(process.env.NODE_ENV === "development");

  // Session debug logger
  const logSessionDebug = useCallback(
    (event, data) => {
      if (debugMode) {
        const timestamp = new Date().toISOString();
        console.log(`SESSION_DEBUG [${timestamp}] ${event}:`, {
          ...data,
          currentPage: window.location.pathname,
          timeoutMinutes: REACT_TIMEOUT_MINUTES,
        });
      }
    },
    [debugMode]
  );

  // ðŸ“— Refs for timeout management and initialization
  const sessionTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const initializingRef = useRef(false);
  const stateUpdateQueueRef = useRef([]);
  const processingUpdateRef = useRef(false);

  // ðŸ›¡ï¸ Token management utilities with error handling
  const storeToken = useCallback((token) => {
    try {
      if (token) {
        sessionStorage.setItem("webvenly_auth_token", token);
        // FIXED: Set login timestamp to enable grace period for race condition protection
        window._webvenly_login_timestamp = Date.now();
        console.log("âœ… Bearer token stored securely");
      }
    } catch (error) {
      console.warn("âš ï¸ Failed to store token:", error);
      toast.error("Session storage error. Please try logging in again.");
    }
  }, []);

  const getStoredToken = useCallback(() => {
    try {
      return sessionStorage.getItem("webvenly_auth_token");
    } catch (error) {
      console.warn("âš ï¸ Failed to retrieve token:", error);
      return null;
    }
  }, []);

  const clearToken = useCallback(() => {
    try {
      sessionStorage.removeItem("webvenly_auth_token");
      sessionStorage.removeItem("user_session");
      // Clear login timestamp
      delete window._webvenly_login_timestamp;
      console.log("ðŸ—‘ï¸ Auth tokens cleared");
    } catch (error) {
      console.warn("âš ï¸ Failed to clear tokens:", error);
    }
  }, []);

  // Enhanced activity tracking with debugging
  const sendActivityHeartbeat = useCallback(async () => {
    try {
      logSessionDebug("ACTIVITY_HEARTBEAT_START", {
        currentTime: new Date().toISOString(),
        hasToken: !!getStoredToken(),
        isAuthenticated: authState.isAuthenticated,
        userId: authState.user?.user_id?.slice(-6),
      });

      const response = await secureAxios.post(
        "/api/auth/activity",
        {
          lastActivity: new Date().toISOString(),
          sessionId: authState.user?.session_id,
          source: "frontend_heartbeat",
        },
        {
          headers: { "X-User-Activity": "true" },
        }
      );

      logSessionDebug("ACTIVITY_HEARTBEAT_SUCCESS", {
        sessionExtended: response.data.sessionExtended,
        expiresAt: response.data.expiresAt,
        timeoutMinutes: response.data.timeoutMinutes,
      });
    } catch (error) {
      logSessionDebug("ACTIVITY_HEARTBEAT_FAILED", {
        error: error.message,
        status: error.response?.status,
        tokenExpired: error.response?.status === 401,
      });

      if (error.response?.status === 401) {
        console.warn(
          "ACTIVITY HEARTBEAT: Token expired during active session!"
        );
      }
    }
  }, [
    authState.user?.session_id,
    authState.isAuthenticated,
    logSessionDebug,
    getStoredToken,
  ]);

  // ðŸŒ Cross-origin detection
  const isCrossOrigin = useMemo(() => {
    const baseURL = secureAxios.defaults.baseURL;
    return (
      baseURL &&
      !baseURL.includes("localhost") &&
      !baseURL.includes(window.location.hostname)
    );
  }, []);

  // ðŸ”„ STABLE STATE UPDATER - Prevents race conditions and flickering
  const updateAuthState = useCallback((updates) => {
    stateUpdateQueueRef.current.push(updates);

    if (processingUpdateRef.current) {
      return; // Already processing, updates will be batched
    }

    processingUpdateRef.current = true;

    // Process all queued updates in next tick
    setTimeout(() => {
      const allUpdates = stateUpdateQueueRef.current.splice(0);
      const finalUpdate = allUpdates.reduce(
        (acc, update) => ({ ...acc, ...update }),
        {}
      );

      setAuthState((currentState) => {
        const newState = { ...currentState, ...finalUpdate };

        // Validate state consistency
        if (newState.isAuthenticated && !newState.user) {
          console.warn(
            "âš ï¸ Inconsistent state: authenticated but no user. Fixing..."
          );
          newState.isAuthenticated = false;
        }

        if (!newState.isAuthenticated && newState.user) {
          console.warn(
            "âš ï¸ Inconsistent state: user exists but not authenticated. Fixing..."
          );
          newState.user = null;
        }

        console.log("ðŸ“Š Auth state updated:", {
          isAuthenticated: newState.isAuthenticated,
          userId: newState.user?.user_id,
          isLoading: newState.isLoading,
          isInitialized: newState.isInitialized,
        });

        return newState;
      });

      processingUpdateRef.current = false;
    }, 0);
  }, []);

  // ðŸ§¹ Clear all timeouts and session data
  const clearAllTimeouts = useCallback(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // ðŸ—‘ï¸ Clear all authentication data without flickering
  const clearAuthData = useCallback(() => {
    if (logoutInProgressRef.current) {
      console.log("âš ï¸ Logout in progress, skipping clearAuthData");
      return;
    }

    console.log("ðŸ§¹ Clearing authentication data...");

    // Use the current refs directly instead of depending on functions
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    try {
      sessionStorage.removeItem("webvenly_auth_token");
      sessionStorage.removeItem("user_session");
    } catch (error) {
      console.warn("âš ï¸ Failed to clear tokens:", error);
    }

    setSessionState({
      showTimeoutWarning: false,
      timeRemaining: 0,
      timeoutStage: "warning",
    });

    // Use functional update to avoid stale closure
    setAuthState({
      isAuthenticated: false,
      user: null,
      isLoading: false,
      isInitialized: true, // Keep initialized as true
    });
  }, []); // Keep empty dependencies but use direct state setters

  // ðŸšª FIXED Logout handler with environment-driven timeouts
  const handleLogout = useCallback(
    async (isTimeout = false) => {
      // Add logout in progress flag to prevent multiple simultaneous logouts
      if (logoutInProgressRef.current) {
        console.log("âš ï¸ Logout already in progress, skipping...");
        return;
      }

      logoutInProgressRef.current = true;

      console.log("ðŸšª Logging out user...", {
        isTimeout,
        isAuthenticated: authState.isAuthenticated,
        userId: authState.user?.user_id,
        sessionTimeout: REACT_TIMEOUT_MINUTES,
        timestamp: new Date().toISOString(),
      });

      try {
        // Server cleanup attempts
        if (authState.isAuthenticated && authState.user?.user_id) {
          console.log("ðŸ“ž Attempting server logout...");

          try {
            const logoutPromise = secureAxios.post("/api/auth/logout", {
              reason: isTimeout ? "client_timeout" : "manual_logout",
              userId: authState.user.user_id,
              sessionTimeoutMinutes: REACT_TIMEOUT_MINUTES,
              timestamp: new Date().toISOString(),
            });

            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error("Logout request timeout")),
                LOGOUT_TIMEOUT_MS
              )
            );

            await Promise.race([logoutPromise, timeoutPromise]);
            console.log("âœ… Server logout completed successfully");
          } catch (logoutError) {
            console.warn("âš ï¸ Server logout failed:", logoutError.message);
            // Continue with client cleanup regardless of server response
          }
        }

        // DIRECT CLIENT CLEANUP - don't call clearAuthData to avoid circular dependency
        console.log("ðŸ§¹ Clearing authentication data directly...");

        // Clear timeouts directly
        if (sessionTimeoutRef.current) {
          clearTimeout(sessionTimeoutRef.current);
          sessionTimeoutRef.current = null;
        }
        if (warningTimeoutRef.current) {
          clearTimeout(warningTimeoutRef.current);
          warningTimeoutRef.current = null;
        }
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
        }

        // Clear tokens directly
        try {
          sessionStorage.removeItem("webvenly_auth_token");
          sessionStorage.removeItem("user_session");
        } catch (error) {
          console.warn("âš ï¸ Failed to clear tokens:", error);
        }

        // Clear session state directly
        setSessionState({
          showTimeoutWarning: false,
          timeRemaining: 0,
          timeoutStage: "warning",
        });

        // Clear auth state directly - single atomic update
        updateAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
        });

        // Show appropriate toast messages
        if (isTimeout) {
          toast.warning(
            `Session expired due to inactivity after ${REACT_TIMEOUT_MINUTES} minutes. Please log in again.`,
            { autoClose: 5000 }
          );
        } else {
          toast.success("Successfully logged out");
        }
      } catch (error) {
        console.error("ðŸš¨ Critical logout error:", error);

        // Force cleanup even on error
        updateAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
        });

        if (isTimeout) {
          toast.error(
            `Session expired after ${REACT_TIMEOUT_MINUTES} minutes. Please log in again.`
          );
        } else {
          toast.error("Logout completed (with server communication issues)");
        }
      } finally {
        logoutInProgressRef.current = false; // Always reset the flag
        window._webvenly_logout_in_progress = false; // Add global flag cleanup
      }
    },
    [authState.isAuthenticated, authState.user, updateAuthState] // âœ… Remove clearAuthData dependency
  );

  // â° FIXED Start session timeout with environment-driven values
  const startSessionTimeout = useCallback(() => {
    if (!authState.isAuthenticated || !authState.user) {
      console.log("â­• Skipping timeout setup - not authenticated");
      return;
    }

    clearAllTimeouts();

    console.log("â° Setting up session timeout (Environment-Driven):", {
      totalMinutes: REACT_TIMEOUT_MINUTES,
      totalMs: SESSION_TOTAL_MS,
      warningStartMs: WARNING_START_MS,
      warningDurationSeconds: Math.floor(WARNING_LEAD_TIME_MS / 1000),
      criticalThresholdSeconds: Math.floor(CRITICAL_THRESHOLD_MS / 1000),
      userId: authState.user.user_id,
      sessionStartTime: new Date().toISOString(),
    });

    // Warning timeout - calculated from environment
    warningTimeoutRef.current = setTimeout(() => {
      const warningDurationSeconds = Math.floor(WARNING_LEAD_TIME_MS / 1000);

      console.log(
        "âš ï¸ Session timeout warning triggered (Environment-Driven)",
        {
          userId: authState.user?.user_id,
          timeRemaining: warningDurationSeconds,
          sessionTimeoutMinutes: REACT_TIMEOUT_MINUTES,
          timestamp: new Date().toISOString(),
        }
      );

      setSessionState({
        showTimeoutWarning: true,
        timeoutStage: "warning",
        timeRemaining: warningDurationSeconds,
      });

      // Start countdown with environment-driven values
      // Start countdown with environment-driven values
      let countdown = warningDurationSeconds;
      countdownRef.current = setInterval(() => {
        countdown--;
        setSessionState((prev) => ({ ...prev, timeRemaining: countdown }));

        // Critical stage threshold - calculated from environment
        const criticalThresholdSeconds = Math.floor(
          CRITICAL_THRESHOLD_MS / 1000
        );

        if (countdown === criticalThresholdSeconds) {
          console.log(
            "ðŸš¨ Entering critical timeout stage (Environment-Driven)",
            {
              userId: authState.user?.user_id,
              timeRemaining: countdown,
              sessionTimeoutMinutes: REACT_TIMEOUT_MINUTES,
              timestamp: new Date().toISOString(),
            }
          );
          setSessionState((prev) => ({ ...prev, timeoutStage: "critical" }));
        }

        // Warning logs at calculated intervals
        const warningIntervals = [30, 10, 5].filter(
          (interval) => interval < warningDurationSeconds
        );

        if (warningIntervals.includes(countdown)) {
          console.log(
            `ðŸš¨ ${countdown} seconds remaining before forced logout (Environment-Driven)`,
            {
              userId: authState.user?.user_id,
              sessionTimeoutMinutes: REACT_TIMEOUT_MINUTES,
              timestamp: new Date().toISOString(),
            }
          );
        }

        // âœ… This block must be INSIDE the setInterval callback
        if (countdown <= 0) {
          console.log(
            "â° Countdown reached zero - forcing logout (Environment-Driven)",
            {
              userId: authState.user?.user_id,
              sessionDuration: `${REACT_TIMEOUT_MINUTES} minutes`,
              totalWarningTime: `${Math.floor(
                WARNING_LEAD_TIME_MS / 1000
              )} seconds`,
              timestamp: new Date().toISOString(),
            }
          );

          clearInterval(countdownRef.current);
          countdownRef.current = null;

          setTimeout(() => {
            if (logoutInProgressRef.current) {
              console.log(
                "âš ï¸ Logout already in progress during countdown, skipping..."
              );
              return;
            }
            handleLogout(true);
          }, 0);
        }
      }, 1000); // âœ… This closes the setInterval callback
    }, WARNING_START_MS); // âœ… This closes the setTimeout callback

    // Final timeout (backup) - calculated from environment
    sessionTimeoutRef.current = setTimeout(() => {
      console.log(
        "â° Backup timeout triggered - forcing logout (Environment-Driven)",
        {
          userId: authState.user?.user_id,
          reason: "backup_timeout_expired",
          sessionTimeoutMinutes: REACT_TIMEOUT_MINUTES,
          timestamp: new Date().toISOString(),
        }
      );

      setTimeout(() => {
        if (logoutInProgressRef.current) {
          console.log(
            "âš ï¸ Logout already in progress during backup timeout, skipping..."
          );
          return;
        }
        handleLogout(true);
      }, 0);
    }, SESSION_TOTAL_MS);
  }, [
    authState.isAuthenticated,
    authState.user,
    clearAllTimeouts,
    handleLogout,
  ]);

  // ðŸ”„ Extend session without state disruption - FIXED VERSION
  const extendSession = useCallback(async () => {
    if (!authState.isAuthenticated || !authState.user) {
      console.log("â­• Cannot extend - not authenticated");
      return { success: false, error: "Not authenticated" }; // âœ… FIXED: Return failure object
    }

    try {
      console.log("ðŸ”„ Extending user session...");

      const config = {};
      if (isCrossOrigin) {
        const token = getStoredToken();
        if (token) {
          config.headers = { Authorization: `Bearer ${token}` };
        }
      }

      const response = await secureAxios.post("/api/auth/refresh", {}, config);

      if (response.data?.success) {
        // Hide warning and restart timeout WITHOUT clearing user state
        setSessionState({
          showTimeoutWarning: false,
          timeRemaining: 0,
          timeoutStage: "warning",
        });

        // Restart session timeout
        startSessionTimeout();

        console.log("âœ… Session extended successfully");
        toast.success("Session extended", { autoClose: 2000 });

        return { success: true }; // âœ… FIXED: Return success object
      } else {
        throw new Error("Session extension failed");
      }
    } catch (error) {
      console.error("âŒ Failed to extend session:", error);
      toast.error("Failed to extend session. Please log in again.", {
        autoClose: 5000,
      });

      return { success: false, error: error.message }; // âœ… FIXED: Return failure object
    }
  }, [
    authState.isAuthenticated,
    authState.user,
    isCrossOrigin,
    getStoredToken,
    startSessionTimeout,
  ]);

  // Activity detection and heartbeat system
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.user) {
      return;
    }

    let activityTimer;
    let lastActivity = Date.now();
    let heartbeatInterval;

    const resetActivityTimer = () => {
      const now = Date.now();

      // Debounce rapid activity
      if (now - lastActivity < 5000) {
        // 5 seconds minimum
        return;
      }

      lastActivity = now;
      logSessionDebug("USER_ACTIVITY_DETECTED", {
        timeSinceLastActivity: now - lastActivity,
        activityType: "user_interaction",
      });

      // Clear existing timer
      clearTimeout(activityTimer);

      // Set timer for 3 minutes of inactivity before sending heartbeat
      activityTimer = setTimeout(() => {
        logSessionDebug("SENDING_ACTIVITY_HEARTBEAT", {
          inactiveFor: "3 minutes",
          timestamp: new Date().toISOString(),
        });
        sendActivityHeartbeat();
      }, 3 * 60 * 1000); // 3 minutes
    };

    // Track these user activities
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "keydown",
      "focus",
    ];

    // Add event listeners
    activityEvents.forEach((event) => {
      document.addEventListener(event, resetActivityTimer, { passive: true });
    });

    // Also send heartbeat every 5 minutes regardless of activity
    heartbeatInterval = setInterval(() => {
      logSessionDebug("SCHEDULED_HEARTBEAT", {
        reason: "periodic_5min_heartbeat",
        timestamp: new Date().toISOString(),
      });
      sendActivityHeartbeat();
    }, 5 * 60 * 1000); // 5 minutes

    // Initial activity detection
    resetActivityTimer();

    logSessionDebug("ACTIVITY_TRACKING_STARTED", {
      eventsTracked: activityEvents,
      heartbeatInterval: "5 minutes",
      activityDebounce: "5 seconds",
    });

    // Cleanup function
    return () => {
      clearTimeout(activityTimer);
      clearInterval(heartbeatInterval);
      activityEvents.forEach((event) => {
        document.removeEventListener(event, resetActivityTimer);
      });
      logSessionDebug("ACTIVITY_TRACKING_STOPPED", {
        reason: "component_unmount",
      });
    };
  }, [
    authState.isAuthenticated,
    authState.user,
    sendActivityHeartbeat,
    logSessionDebug,
  ]);

  // ðŸ” Login function with stable state management
  const login = useCallback(
    async (email, password) => {
      console.log("ðŸ” Attempting login for:", email);

      // Set loading state
      updateAuthState({ isLoading: true });
      // FIXED: Reset logout flags before login attempt to prevent race conditions
      window._webvenly_logout_in_progress = false;

      try {
        const response = await secureAxios.post("/api/auth/login", {
          email: email.toLowerCase().trim(),
          password,
        });

        if (response.data?.success && response.data?.user) {
          const userData = response.data.user;
          const token = response.data.token;

          // Store token if provided (for cross-origin)
          if (token) {
            storeToken(token);
          }

          // Store session data
          try {
            sessionStorage.setItem("user_session", JSON.stringify(userData));
          } catch (error) {
            console.warn("âš ï¸ Failed to store session:", error);
          }
          // FIXED: Small delay to ensure token is available to interceptors before state update triggers API calls
          await new Promise((resolve) => setTimeout(resolve, 150));

          // Single atomic state update - NO FLICKERING
          updateAuthState({
            isAuthenticated: true,
            user: userData,
            isLoading: false,
            isInitialized: true,
          });

          console.log("âœ… Login successful for user:", userData.user_id);
          toast.success("Login successful!");

          return { success: true, user: userData };
        } else {
          throw new Error("Invalid login response");
        }
      } catch (error) {
        console.error("âŒ Login failed:", error);

        const errorMessage =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Login failed. Please check your credentials.";

        toast.error(errorMessage);

        updateAuthState({ isLoading: false });
        return { success: false, error: errorMessage };
      }
    },
    [updateAuthState, storeToken]
  );

  // ðŸš€ Initialize authentication on app start
  const initializeAuth = useCallback(async () => {
    if (initializingRef.current || authState.isInitialized) {
      return;
    }

    initializingRef.current = true;
    console.log("ðŸš€ Initializing authentication...");

    try {
      // Check for existing session first
      const sessionData = sessionStorage.getItem("user_session");

      if (sessionData) {
        try {
          const session = JSON.parse(sessionData);
          console.log("ðŸ“± Found existing session for user:", session.user_id);

          // Verify session with server
          const config = {};
          if (isCrossOrigin) {
            const token = getStoredToken();
            if (token) {
              config.headers = { Authorization: `Bearer ${token}` };
            }
          }

          const response = await secureAxios.get("/api/auth/profile", config);

          if (response.data) {
            const userData = response.data;

            // Restore authenticated state in single update
            updateAuthState({
              isAuthenticated: true,
              user: userData,
              isLoading: false,
              isInitialized: true,
            });

            console.log("âœ… Session restored for user:", userData.user_id);
            return;
          }
        } catch (sessionError) {
          console.warn("âš ï¸ Session verification failed:", sessionError);
          // Clear invalid session data
          clearToken();
        }
      }

      // No valid session found
      updateAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        isInitialized: true,
      });
    } catch (error) {
      console.error("âŒ Auth initialization failed:", error);
      updateAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        isInitialized: true,
      });
    } finally {
      initializingRef.current = false;
    }
  }, [
    authState.isInitialized,
    isCrossOrigin,
    getStoredToken,
    clearToken,
    updateAuthState,
  ]);

  // ðŸŽ¯ Effect: Start session timeout when user becomes authenticated
  useEffect(() => {
    if (
      authState.isAuthenticated &&
      authState.user &&
      authState.isInitialized
    ) {
      console.log("ðŸ‘¤ User authenticated - starting session timeout");
      startSessionTimeout();
    }
  }, [
    authState.isAuthenticated,
    authState.user,
    authState.isInitialized,
    startSessionTimeout,
  ]);

  // ðŸ Effect: Initialize auth on mount and check backend timeout
  useEffect(() => {
    initializeAuth();

    // Check backend timeout after component mounts
    // ENHANCED: Check backend timeout with validation and error handling
    const checkBackendTimeout = async () => {
      try {
        const response = await secureAxios.get("/api/auth/timeout-config");
        const data = response.data;

        const backendTimeout = data.backend_timeout;

        if (backendTimeout && backendTimeout !== REACT_TIMEOUT_MINUTES) {
          const errorMsg = `ðŸš¨ CRITICAL TIMEOUT MISMATCH: Backend=${backendTimeout}min, Frontend=${REACT_TIMEOUT_MINUTES}min`;
          console.error(errorMsg);

          // Show user-facing error for critical mismatch
          toast.error(
            `Configuration Error: Session timeout mismatch detected. Please contact support.`,
            { autoClose: 10000 }
          );

          // In development, show detailed info
          if (process.env.NODE_ENV === "development") {
            toast.warn(
              `DEV: Backend timeout (${backendTimeout}m) != Frontend timeout (${REACT_TIMEOUT_MINUTES}m)`,
              { autoClose: 15000 }
            );
          }
        } else if (backendTimeout) {
          console.log(
            `âœ… TIMEOUT SYNCHRONIZED: Backend=${backendTimeout}min, Frontend=${REACT_TIMEOUT_MINUTES}min`
          );
        }

        // Validate HIPAA compliance
        if (backendTimeout > 15 || REACT_TIMEOUT_MINUTES > 15) {
          console.warn(
            `âš ï¸ HIPAA WARNING: Timeout exceeds 15 minutes - Backend: ${backendTimeout}min, Frontend: ${REACT_TIMEOUT_MINUTES}min`
          );
        }
      } catch (error) {
        console.warn(
          "Could not check backend timeout configuration:",
          error.message
        );
        // Fallback: assume backend matches if we can't verify
      }
    };

    // Delay the check to ensure server is ready
    setTimeout(checkBackendTimeout, 1000);
  }, [initializeAuth]);

  // ðŸ§¹ Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);

  // NEW: Listen for logout events from secureAxios
  useEffect(() => {
    const handleLogoutEvent = (event) => {
      const { reason, code, message } = event.detail || {};
      console.log("Received logout event from secureAxios:", {
        reason,
        code,
        message,
      });

      if (
        !logoutInProgressRef.current &&
        !window._webvenly_logout_in_progress
      ) {
        handleLogout(true); // Mark as timeout logout
      }
    };

    window.addEventListener("auth-logout", handleLogoutEvent);

    return () => {
      window.removeEventListener("auth-logout", handleLogoutEvent);
    };
  }, [handleLogout]);

  // ðŸ“Š Context value with stable references
  const contextValue = useMemo(
    () => ({
      // Core auth state (destructured for backward compatibility)
      isAuthenticated: authState.isAuthenticated,
      user: authState.user,
      loading: authState.isLoading,
      loadingUser: authState.isLoading, // Legacy alias

      // Session timeout state
      timeoutMinutes,
      showTimeoutWarning: sessionState.showTimeoutWarning,
      timeoutStage: sessionState.timeoutStage,
      timeRemaining: sessionState.timeRemaining,

      // Auth methods
      login,
      logout: handleLogout,
      extendSession,
      clearAuthData,

      // Session management
      resetSessionTimeout: startSessionTimeout,

      // Convenience properties for components
      tenant_id: authState.user?.tenant_id || null,
      role: authState.user?.role || null,
      tenant_type: authState.user?.tenant_type || null,

      // State flags
      isInitialized: authState.isInitialized,
    }),
    [
      authState,
      sessionState,
      timeoutMinutes,
      login,
      handleLogout,
      extendSession,
      clearAuthData,
      startSessionTimeout,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <SessionTimeoutWarning
        show={sessionState.showTimeoutWarning}
        onExtend={extendSession}
        onLogout={handleLogout}
        timeoutMinutes={timeoutMinutes}
        timeRemaining={sessionState.timeRemaining}
        stage={sessionState.timeoutStage}
      />
    </AuthContext.Provider>
  );
};

export { AuthContext };
